import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { API } from "@/utils/constants";

const b64ToUint8 = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const PushNotificationToggle = () => {
  const supported = "serviceWorker" in navigator && "PushManager" in window;
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setEnabled(!!sub))
    ).catch(() => {});
  }, [supported]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (on) {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") { toast.error("Notifications blocked — allow them in your browser settings"); setBusy(false); return; }
        const { data } = await axios.get(`${API}/push/vapid-public-key`);
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.public_key) });
        await axios.post(`${API}/push/subscribe`, sub.toJSON());
        setEnabled(true);
        toast.success("Push notifications enabled on this device");
      } else {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }).catch(() => {});
          await sub.unsubscribe();
        }
        setEnabled(false);
        toast.success("Push notifications disabled");
      }
    } catch (e) { toast.error("Failed to update push settings"); }
    setBusy(false);
  };

  if (!supported) return null;
  return (
    <div className="flex items-center justify-between p-4 bg-card border rounded-xl" data-testid="push-toggle-row">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center"><BellRing className="h-4 w-4 text-primary" /></div>
        <div>
          <div className="text-sm font-medium">Push notifications</div>
          <div className="text-xs text-muted-foreground">Get chat alerts on this device — even when the app is closed</div>
        </div>
      </div>
      <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} data-testid="push-toggle-switch" />
    </div>
  );
};
